import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useSEO } from '@/hooks/useSEO';

export default function CaixinhaDeAjudaPage() {
  const [personName, setPersonName] = useState('');
  const [personPhone, setPersonPhone] = useState('');
  const [personAddress, setPersonAddress] = useState('');
  const [personPix, setPersonPix] = useState('');
  const [pixOwnerName, setPixOwnerName] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [referrerPhone, setReferrerPhone] = useState('');
  const [websiteAlt, setWebsiteAlt] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState(false);

  useSEO({
    title: 'Caixinha de Ajuda | HomeOffice Premium',
    description: 'Conhece alguém que precisa de ajuda? Indique e a HomeOffice Premium pode ajudar. Preencha os dados da pessoa e nós entramos em contato.',
    keywords: 'caixinha de ajuda, doação, ajuda, pix, home office premium',
    path: '/caixinha-de-ajuda',
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFormOk(false);

    if (websiteAlt.trim()) {
      setFormOk(true);
      setPersonName('');
      setPersonPhone('');
      setPersonAddress('');
      setPersonPix('');
      setPixOwnerName('');
      setReferrerName('');
      setReferrerPhone('');
      setWebsiteAlt('');
      return;
    }

    if (!personName.trim()) {
      setFormError('Informe o nome da pessoa que precisa de ajuda.');
      return;
    }
    if (!personPix.trim()) {
      setFormError('Informe a chave Pix da pessoa.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('help-requests', {
        body: {
          action: 'submit',
          person_name: personName.trim(),
          person_phone: personPhone.trim(),
          person_address: personAddress.trim(),
          person_pix: personPix.trim(),
          pix_owner_name: pixOwnerName.trim(),
          referrer_name: referrerName.trim(),
          referrer_phone: referrerPhone.trim(),
        },
      });

      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);

      setFormOk(true);
      setPersonName('');
      setPersonPhone('');
      setPersonAddress('');
      setPersonPix('');
      setPixOwnerName('');
      setReferrerName('');
      setReferrerPhone('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Erro ao enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-hop-bg-alt overflow-x-hidden">
      <Navbar />

      <main className="pt-24 md:pt-28">
        {/* Hero */}
        <section className="bg-hop-graphite text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
                <i className="ri-heart-3-line text-hop-orange"></i>
                <span className="text-xs md:text-sm font-medium text-white/80">
                  Ação social da Home Office Premium
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-5">
                Caixinha de Ajuda 💛
              </h1>
              <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
                Conhece alguém que está passando por uma necessidade e precisa de uma mão?
                Indique essa pessoa e a HomeOffice Premium pode ajudar. Preencha os dados
                abaixo e nosso time vai avaliar cada solicitação com carinho.
              </p>
            </div>
          </div>
        </section>

        {/* Formulário */}
        <section className="max-w-3xl mx-auto px-5 md:px-8 -mt-8 pb-16 md:pb-24">
          <div className="bg-white rounded-2xl border border-hop-border p-6 md:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center bg-hop-orange/10 text-hop-orange rounded-xl">
                <i className="ri-hand-heart-line text-xl"></i>
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-lg">
                  Indicar uma pessoa para receber ajuda
                </h2>
                <p className="text-sm text-gray-500">
                  Os campos com * são obrigatórios.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot anti-spam */}
              <div className="hop-hp-field" aria-hidden="true">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Site
                </label>
                <input
                  type="text"
                  name="website_alt"
                  value={websiteAlt}
                  onChange={(e) => setWebsiteAlt(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
                />
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-heart-line text-hop-orange"></i>
                  Dados da pessoa que precisa de ajuda
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Nome da pessoa *
                    </label>
                    <input
                      type="text"
                      name="person_name"
                      value={personName}
                      onChange={(e) => setPersonName(e.target.value)}
                      placeholder="Ex: Maria Silva"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Telefone / WhatsApp
                    </label>
                    <input
                      type="text"
                      name="person_phone"
                      value={personPhone}
                      onChange={(e) => setPersonPhone(e.target.value)}
                      placeholder="(81) 99999-9999"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="person_address"
                    value={personAddress}
                    onChange={(e) => setPersonAddress(e.target.value)}
                    placeholder="Rua, número, bairro, cidade"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                  />
                </div>
              </div>

              <div className="border-t border-hop-border pt-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-qr-code-line text-hop-orange"></i>
                  Para onde enviar a ajuda (Pix)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Chave Pix *
                    </label>
                    <input
                      type="text"
                      name="person_pix"
                      value={personPix}
                      onChange={(e) => setPersonPix(e.target.value)}
                      placeholder="CPF, e-mail, celular ou aleatória"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Nome do titular do Pix
                    </label>
                    <input
                      type="text"
                      name="pix_owner_name"
                      value={pixOwnerName}
                      onChange={(e) => setPixOwnerName(e.target.value)}
                      placeholder="Ex: Maria Silva"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-hop-border pt-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-shared-line text-hop-orange"></i>
                  Quem está indicando (opcional)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Seu nome
                    </label>
                    <input
                      type="text"
                      name="referrer_name"
                      value={referrerName}
                      onChange={(e) => setReferrerName(e.target.value)}
                      placeholder="Seu nome"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      Seu telefone
                    </label>
                    <input
                      type="text"
                      name="referrer_phone"
                      value={referrerPhone}
                      onChange={(e) => setReferrerPhone(e.target.value)}
                      placeholder="(81) 99999-9999"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-hop-orange focus:ring-2 focus:ring-hop-orange/20 transition-all"
                    />
                  </div>
                </div>
              </div>

              {formError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </div>
              )}

              {formOk && (
                <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <i className="ri-checkbox-circle-line text-base mt-0.5"></i>
                  <span>
                    Solicitação enviada com sucesso! Obrigado por indicar alguém. Nossa
                    equipe vai analisar cada caso com muito carinho. 💛
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-hop-orange-cta text-white text-sm font-semibold px-6 py-3.5 rounded-xl hover:bg-hop-orange-hover transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Enviar solicitação de ajuda
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6 leading-relaxed">
            Ao enviar, você concorda que os dados informados sejam utilizados apenas para
            análise e realização da ajuda. A HomeOffice Premium avalia cada solicitação e
            não há garantia de atendimento a todos os pedidos.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}